/**
 * Marketing Terminal — Settings & Connections page
 *
 * Two surfaces:
 *   1. Connections — per-platform credential management. Paste keys, test
 *      connection, see status. Backend writes secrets to AWS Secrets Manager.
 *   2. Agent Keys — generate per-scope Cowork API keys for the agent bridge.
 *      Keys shown ONCE on creation; only hash stored.
 *
 * Backend:
 *   GET    /api/admin/marketing/platforms/catalog    — known platforms + fields
 *   GET    /api/admin/marketing/connections          — list configured connections
 *   POST   /api/admin/marketing/connections          — register new account
 *   PUT    /api/admin/marketing/connections/:id/credentials  — paste keys
 *   POST   /api/admin/marketing/connections/:id/test
 *   DELETE /api/admin/marketing/connections/:id
 *   GET    /api/admin/marketing/agent-keys
 *   POST   /api/admin/marketing/agent-keys
 *   POST   /api/admin/marketing/agent-keys/:id/revoke
 */

import React, { useState } from 'react';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  Key,
  Plug,
  Plus,
  RotateCcw,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

// ── Types ────────────────────────────────────────────────────────────────────

interface CredentialField {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
  configured: boolean;
}

interface PlatformStatus {
  platform: string;
  fields: CredentialField[];
  all_configured: boolean;
  any_required_missing: boolean;
}

interface Connection {
  id: string;
  platform: string;
  account_handle: string | null;
  account_label: string | null;
  account_type: string;
  is_default: boolean;
  is_connected: boolean;
  connection_status: string | null;
  oauth_token_expires_at: string | null;
  last_synced_at: string | null;
  last_sync_error: string | null;
  notes: string | null;
}

interface AgentKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  is_active: boolean;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

// ── QueryClient ──────────────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

export function MarketingSettings() {
  return (
    <QueryClientProvider client={queryClient}>
      <SettingsInner />
    </QueryClientProvider>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

function SettingsInner() {
  const [tab, setTab] = useState<'connections' | 'agent_keys'>('connections');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}>
          Connections & Keys
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Platform API credentials and Cowork agent bridge keys. All credentials write to AWS Secrets Manager.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {[
          { key: 'connections', label: 'Connections', icon: Plug },
          { key: 'agent_keys', label: 'Agent Keys', icon: Key },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key as 'connections' | 'agent_keys')}
              className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                active ? '' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              style={active ? { borderColor: 'var(--cedar-primary)', color: 'var(--cedar-primary)' } : {}}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'connections' && <ConnectionsTab />}
      {tab === 'agent_keys' && <AgentKeysTab />}
    </div>
  );
}

// ── Connections tab ──────────────────────────────────────────────────────────

interface CatalogResponse { success: boolean; data: PlatformStatus[]; }
interface ConnectionsResponse { success: boolean; data: Connection[]; }

function ConnectionsTab() {
  const catalog = useQuery<CatalogResponse>({
    queryKey: ['marketing-platform-catalog'],
    queryFn: () => adminFetch<CatalogResponse>('/api/admin/marketing/platforms/catalog'),
  });

  const connections = useQuery<ConnectionsResponse>({
    queryKey: ['marketing-connections'],
    queryFn: () => adminFetch<ConnectionsResponse>('/api/admin/marketing/connections'),
  });

  if (catalog.isLoading) return <div className="text-sm text-gray-500">Loading platforms...</div>;
  if (catalog.error) return <div className="text-sm text-red-600">Failed to load platform catalog</div>;

  const platformsToShow = catalog.data?.data ?? [];

  return (
    <div className="space-y-3">
      {platformsToShow.map(platform => (
        <PlatformCard
          key={platform.platform}
          platform={platform}
          connections={(connections.data?.data ?? []).filter(c => c.platform === platform.platform)}
        />
      ))}
    </div>
  );
}

function PlatformCard({ platform, connections }: { platform: PlatformStatus; connections: Connection[] }) {
  const [expanded, setExpanded] = useState(false);
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});
  const [showAddAccount, setShowAddAccount] = useState(false);
  const queryClient = useQueryClient();

  const saveCredentials = useMutation({
    mutationFn: async ({ connectionId }: { connectionId: string }) => {
      const fields = Object.entries(credentialValues)
        .filter(([, v]) => v && v.trim())
        .map(([key, value]) => ({ key, value }));
      if (fields.length === 0) throw new Error('No credentials to save');
      return adminFetch(`/api/admin/marketing/connections/${connectionId}/credentials`, {
        method: 'PUT',
        body: JSON.stringify({ fields }),
        headers: { 'Content-Type': 'application/json' },
      });
    },
    onSuccess: () => {
      setCredentialValues({});
      queryClient.invalidateQueries({ queryKey: ['marketing-platform-catalog'] });
      queryClient.invalidateQueries({ queryKey: ['marketing-connections'] });
    },
  });

  const dotColor = platform.all_configured ? 'var(--cedar-primary)' : platform.any_required_missing ? 'var(--status-error)' : 'var(--status-warning)';

  return (
    <div className="rounded-lg bg-white" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between p-4 hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: dotColor }} />
          <span className="text-sm font-semibold text-gray-900">{platform.platform}</span>
          <span className="text-xs text-gray-500">
            {platform.fields.filter(f => f.configured).length}/{platform.fields.length} credentials configured
          </span>
          {connections.length > 0 && (
            <span className="text-xs text-gray-400">· {connections.length} account{connections.length !== 1 ? 's' : ''}</span>
          )}
        </div>
        <span className="text-xs text-gray-400">{expanded ? 'Collapse' : 'Expand'}</span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-4">
          {/* Credential fields */}
          <div className="mb-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              App-level credentials (written to AWS Secrets Manager)
            </div>
            <div className="space-y-2">
              {platform.fields.map(field => (
                <div key={field.key} className="flex items-center gap-2">
                  {field.configured ? (
                    <Check className="h-4 w-4 flex-shrink-0 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 flex-shrink-0 text-gray-300" />
                  )}
                  <label className="w-56 flex-shrink-0 text-xs font-medium text-gray-700">
                    {field.label}
                    {field.required && <span className="text-red-500"> *</span>}
                  </label>
                  <input
                    type={field.secret ? 'password' : 'text'}
                    placeholder={field.configured ? '••••••••  (replace to rotate)' : 'Paste here'}
                    className="flex-1 rounded border border-gray-200 px-2 py-1.5 text-xs focus:border-green-600 focus:outline-none"
                    value={credentialValues[field.key] ?? ''}
                    onChange={e => setCredentialValues({ ...credentialValues, [field.key]: e.target.value })}
                  />
                </div>
              ))}
            </div>
            {connections.length > 0 && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => saveCredentials.mutate({ connectionId: connections[0].id })}
                  disabled={saveCredentials.isPending || Object.keys(credentialValues).length === 0}
                  className="rounded px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                  style={{ backgroundColor: 'var(--cedar-primary)' }}
                >
                  {saveCredentials.isPending ? 'Saving...' : 'Save credentials'}
                </button>
                {saveCredentials.isError && (
                  <span className="text-xs text-red-600">{(saveCredentials.error as Error)?.message}</span>
                )}
                {saveCredentials.isSuccess && (
                  <span className="text-xs text-green-600">Saved to Secrets Manager</span>
                )}
              </div>
            )}
            {connections.length === 0 && (
              <p className="mt-3 text-xs text-amber-700">
                Register an account below first, then paste credentials here.
              </p>
            )}
          </div>

          {/* Connected accounts */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Connected accounts
              </div>
              <button
                onClick={() => setShowAddAccount(!showAddAccount)}
                className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
              >
                <Plus className="h-3 w-3" /> Add account
              </button>
            </div>

            {showAddAccount && (
              <AddAccountForm
                platform={platform.platform}
                onClose={() => {
                  setShowAddAccount(false);
                  queryClient.invalidateQueries({ queryKey: ['marketing-connections'] });
                }}
              />
            )}

            {connections.length === 0 ? (
              <p className="text-xs italic text-gray-400">No accounts registered</p>
            ) : (
              <div className="space-y-2">
                {connections.map(conn => (
                  <ConnectionRow key={conn.id} connection={conn} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AddAccountForm({ platform, onClose }: { platform: string; onClose: () => void }) {
  const [handle, setHandle] = useState('');
  const [label, setLabel] = useState('');
  const [accountType, setAccountType] = useState<'personal' | 'brand' | 'community' | 'service'>('brand');

  const create = useMutation({
    mutationFn: () =>
      adminFetch('/api/admin/marketing/connections', {
        method: 'POST',
        body: JSON.stringify({
          platform,
          account_handle: handle || null,
          account_label: label || null,
          account_type: accountType,
          is_default: false,
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      setHandle('');
      setLabel('');
      onClose();
    },
  });

  return (
    <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-3">
      <div className="grid grid-cols-3 gap-2">
        <input
          placeholder="Handle (e.g. paulrusheleau)"
          className="rounded border border-gray-200 px-2 py-1.5 text-xs"
          value={handle}
          onChange={e => setHandle(e.target.value)}
        />
        <input
          placeholder='Label (e.g. "Paul (Founder)")'
          className="rounded border border-gray-200 px-2 py-1.5 text-xs"
          value={label}
          onChange={e => setLabel(e.target.value)}
        />
        <select
          className="rounded border border-gray-200 px-2 py-1.5 text-xs"
          value={accountType}
          onChange={e => setAccountType(e.target.value as 'personal' | 'brand' | 'community' | 'service')}
        >
          <option value="brand">Brand</option>
          <option value="personal">Personal (Founder)</option>
          <option value="community">Community</option>
          <option value="service">Service (no account)</option>
        </select>
      </div>
      <div className="mt-2 flex gap-2">
        <button
          onClick={() => create.mutate()}
          disabled={create.isPending}
          className="rounded px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: 'var(--cedar-primary)' }}
        >
          {create.isPending ? 'Creating...' : 'Create'}
        </button>
        <button onClick={onClose} className="rounded px-3 py-1.5 text-xs text-gray-500">
          Cancel
        </button>
        {create.isError && (
          <span className="text-xs text-red-600">{(create.error as Error)?.message}</span>
        )}
      </div>
    </div>
  );
}

interface InitiateOAuthResponse {
  success: boolean;
  data: {
    authorization_url: string;
    state_token: string;
    redirect_uri: string;
    expires_at: string;
  };
}

function ConnectionRow({ connection }: { connection: Connection }) {
  const queryClient = useQueryClient();

  const authorize = useMutation({
    mutationFn: () =>
      adminFetch<InitiateOAuthResponse>('/api/admin/marketing/oauth/initiate', {
        method: 'POST',
        body: JSON.stringify({
          connection_id: connection.id,
          redirect_after_success: '/admin/marketing/settings',
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: data => {
      // Redirect the browser to the platform's authorization page
      if (data?.data?.authorization_url) {
        window.location.href = data.data.authorization_url;
      }
    },
  });

  const refresh = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/marketing/oauth/connections/${connection.id}/refresh`, {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-connections'] }),
  });

  const test = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/marketing/connections/${connection.id}/test`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-connections'] }),
  });

  const remove = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/marketing/connections/${connection.id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-connections'] }),
  });

  const status = connection.connection_status ?? 'red';
  const statusColor = status === 'green' ? 'var(--cedar-primary)' : status === 'yellow' ? 'var(--status-warning)' : 'var(--status-error)';
  const tokenExpiringSoon =
    connection.oauth_token_expires_at &&
    new Date(connection.oauth_token_expires_at).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000;

  return (
    <div className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2">
      <div className="flex items-center gap-3">
        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: statusColor }} />
        <div>
          <div className="text-sm font-medium text-gray-900">
            {connection.account_label || connection.account_handle || '(unnamed)'}
            <span className="ml-2 text-xs text-gray-400">({connection.account_type})</span>
            {connection.is_default && (
              <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                default
              </span>
            )}
          </div>
          {connection.last_sync_error && (
            <div className="text-xs text-amber-600">
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              {connection.last_sync_error}
            </div>
          )}
          {tokenExpiringSoon && !connection.last_sync_error && (
            <div className="text-xs text-amber-600">
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              Token expires {new Date(connection.oauth_token_expires_at!).toLocaleDateString()} — re-authorize soon
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!connection.is_connected ? (
          <button
            onClick={() => authorize.mutate()}
            disabled={authorize.isPending}
            className="rounded px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: 'var(--cedar-primary)' }}
          >
            {authorize.isPending ? 'Loading...' : 'Authorize'}
          </button>
        ) : (
          <>
            <button
              onClick={() => test.mutate()}
              disabled={test.isPending}
              className="rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              {test.isPending ? 'Testing...' : 'Test'}
            </button>
            {tokenExpiringSoon && (
              <button
                onClick={() => refresh.mutate()}
                disabled={refresh.isPending}
                className="rounded px-2 py-1 text-xs font-medium hover:bg-amber-50 disabled:opacity-50"
                style={{ color: 'var(--status-warning)' }}
              >
                {refresh.isPending ? '...' : 'Refresh'}
              </button>
            )}
            <button
              onClick={() => authorize.mutate()}
              disabled={authorize.isPending}
              className="rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              title="Re-authorize from scratch"
            >
              Re-auth
            </button>
          </>
        )}
        <button
          onClick={() => {
            if (confirm(`Disconnect ${connection.platform}? Secrets will be deleted.`)) remove.mutate();
          }}
          disabled={remove.isPending}
          className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ── Agent Keys tab ───────────────────────────────────────────────────────────

interface AgentKeysResponse { success: boolean; data: AgentKey[]; }

function AgentKeysTab() {
  const keys = useQuery<AgentKeysResponse>({
    queryKey: ['marketing-agent-keys'],
    queryFn: () => adminFetch<AgentKeysResponse>('/api/admin/marketing/agent-keys'),
  });
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
        <AlertTriangle className="mr-1 inline h-3 w-3" />
        <strong>Cowork agent bridge keys.</strong> Each scheduled task in Cowork (content writer,
        listening scanner, etc.) should have its own scoped key. If a key is compromised, blast
        radius is limited to that scope. Keys are shown ONCE on creation — store immediately in
        the Cowork environment file.
      </div>

      {newKeyValue && (
        <div className="rounded-lg border-2 border-green-500 bg-green-50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <strong className="text-sm text-green-900">New agent key — copy now, never shown again</strong>
            <button
              onClick={() => setNewKeyValue(null)}
              className="text-green-700 hover:text-green-900"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 rounded bg-white p-2 font-mono text-xs">
            <code className="flex-1 break-all">{newKeyValue}</code>
            <button
              onClick={() => navigator.clipboard.writeText(newKeyValue)}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium hover:bg-green-100"
              style={{ color: 'var(--cedar-primary)' }}
            >
              <Copy className="h-3 w-3" /> Copy
            </button>
          </div>
          <p className="mt-2 text-xs text-green-800">
            Add to Cowork's environment file as <code className="rounded bg-white px-1">MARKETING_AGENT_KEY</code>.
          </p>
        </div>
      )}

      <div className="flex justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Active keys</h2>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1 rounded px-3 py-1.5 text-xs font-medium text-white"
          style={{ backgroundColor: 'var(--cedar-primary)' }}
        >
          <Plus className="h-3 w-3" /> Generate key
        </button>
      </div>

      {showCreate && (
        <CreateKeyForm
          onCreated={key => {
            setNewKeyValue(key);
            setShowCreate(false);
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      <div className="space-y-2">
        {keys.data?.data?.map(key => <AgentKeyRow key={key.id} agentKey={key} />)}
        {keys.data?.data?.length === 0 && (
          <p className="text-sm italic text-gray-400">No agent keys yet</p>
        )}
      </div>
    </div>
  );
}

const SCOPE_OPTIONS = [
  { key: 'social.write', label: 'Push social drafts' },
  { key: 'ideas.write', label: 'Push content ideas' },
  { key: 'listening.write', label: 'Push listening alerts' },
  { key: 'engagement.read', label: 'Read engagement queue' },
  { key: 'engagement.write', label: 'Push engagement drafts' },
  { key: 'context.read', label: 'Read brand kit + voice' },
  { key: 'calendar.read', label: 'Read editorial calendar' },
  { key: 'performance.read', label: 'Read performance metrics' },
];

function CreateKeyForm({ onCreated, onCancel }: { onCreated: (key: string) => void; onCancel: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['social.write', 'context.read']);
  const [expiresInDays, setExpiresInDays] = useState(365);

  interface CreateKeyResponse {
    success: boolean;
    data: { key: string };
  }

  const create = useMutation({
    mutationFn: () =>
      adminFetch<CreateKeyResponse>('/api/admin/marketing/agent-keys', {
        method: 'POST',
        body: JSON.stringify({ name, scopes, expires_in_days: expiresInDays }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['marketing-agent-keys'] });
      onCreated(data.data.key);
    },
  });

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Name</label>
          <input
            placeholder="e.g. cowork-content-writer"
            className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Scopes</label>
          <div className="grid grid-cols-2 gap-1">
            {SCOPE_OPTIONS.map(opt => (
              <label key={opt.key} className="flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={scopes.includes(opt.key)}
                  onChange={e => {
                    if (e.target.checked) setScopes([...scopes, opt.key]);
                    else setScopes(scopes.filter(s => s !== opt.key));
                  }}
                />
                <code className="text-xs">{opt.key}</code>
                <span className="text-gray-500">— {opt.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Expires in (days)</label>
          <input
            type="number"
            min={1}
            max={730}
            className="w-32 rounded border border-gray-200 px-2 py-1.5 text-xs"
            value={expiresInDays}
            onChange={e => setExpiresInDays(parseInt(e.target.value) || 365)}
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => create.mutate()}
            disabled={!name || scopes.length === 0 || create.isPending}
            className="rounded px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: 'var(--cedar-primary)' }}
          >
            {create.isPending ? 'Generating...' : 'Generate key'}
          </button>
          <button onClick={onCancel} className="rounded px-3 py-1.5 text-xs text-gray-500">
            Cancel
          </button>
          {create.isError && (
            <span className="text-xs text-red-600">{(create.error as Error)?.message}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentKeyRow({ agentKey }: { agentKey: AgentKey }) {
  const queryClient = useQueryClient();
  const revoke = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/marketing/agent-keys/${agentKey.id}/revoke`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-agent-keys'] }),
  });

  const isExpired = agentKey.expires_at && new Date(agentKey.expires_at) < new Date();
  const status = agentKey.revoked_at ? 'revoked' : isExpired ? 'expired' : agentKey.is_active ? 'active' : 'inactive';

  return (
    <div
      className={`flex items-center justify-between rounded-lg border p-3 ${
        status === 'active' ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'
      }`}
    >
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{agentKey.name}</span>
          <span
            className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase"
            style={{
              backgroundColor: status === 'active' ? 'var(--cedar-subtle)' : 'var(--cedar-limestone)',
              color: status === 'active' ? 'var(--cedar-primary)' : 'var(--text-secondary)',
            }}
          >
            {status}
          </span>
          <code className="text-xs text-gray-400">{agentKey.key_prefix}…</code>
        </div>
        <div className="mt-1 text-xs text-gray-500">
          Scopes: {agentKey.scopes.join(', ')}
          {agentKey.last_used_at && (
            <> · Last used {new Date(agentKey.last_used_at).toLocaleDateString()}</>
          )}
          {agentKey.expires_at && !isExpired && (
            <> · Expires {new Date(agentKey.expires_at).toLocaleDateString()}</>
          )}
        </div>
      </div>
      {status === 'active' && (
        <button
          onClick={() => {
            if (confirm(`Revoke "${agentKey.name}"? This is irreversible.`)) revoke.mutate();
          }}
          disabled={revoke.isPending}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          <RotateCcw className="h-3 w-3" /> Revoke
        </button>
      )}
    </div>
  );
}

export default MarketingSettings;
